import { DirectionProvider } from "@base-ui/react/direction-provider"
import {
  formatNotice,
  type Notice as SharedNotice,
  NoticeError as SharedNoticeError,
} from "@norn/widget-renderer/messages"
import i18next, { type FormatterModule } from "i18next"
import { Direction } from "radix-ui"
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { I18nextProvider, initReactI18next, useTranslation } from "react-i18next"
import { ar } from "@/i18n/ar"
import { en, type MessageKey } from "@/i18n/en"

export type Locale = "en" | "ar"
export type Digits = "latn" | "arab"

export interface LocalePreference {
  readonly locale: Locale
  /** Arabic only: Latin (0-9) or Arabic-Indic (٠-٩) digits. */
  readonly digits: Digits
}

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "translation"
    resources: { translation: typeof en }
    keySeparator: false
  }
}

/**
 * Interpolated counts follow the chosen digits (Arabic-Indic or Latin). Only count-like variables are
 * formatted: versions, positions, ids and data values keep their exact Latin digits.
 */
export function isCountVariable(name: string): boolean {
  return (
    name === "count" ||
    name.endsWith("Count") ||
    name === "cells" ||
    name === "page" ||
    name === "limit" ||
    name === "shown"
  )
}
let countFormat = new Intl.NumberFormat("en")
function applyDigits(preference: LocalePreference): void {
  countFormat = new Intl.NumberFormat(formattingTag(preference))
}
const countFormatter: FormatterModule = {
  type: "formatter",
  init: () => undefined,
  add: () => undefined,
  addCached: () => undefined,
  format: (value, _format, _lng, options) =>
    typeof value === "number" &&
    typeof options?.interpolationkey === "string" &&
    isCountVariable(options.interpolationkey)
      ? countFormat.format(value)
      : value,
}

const instance = i18next.createInstance()
void instance
  .use(initReactI18next)
  .use(countFormatter)
  .init({
    resources: { en: { translation: en }, ar: { translation: ar } },
    lng: "en",
    fallbackLng: "en",
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false, alwaysFormat: true },
    returnNull: false,
  })

const DEVICE_KEY = "norn.admin.locale/1"
/** The device's last choice, else the browser language; its digits apply before the first render. */
function initialPreference(): LocalePreference {
  const preference = parse(localStorage.getItem(DEVICE_KEY)) ?? browserPreference()
  applyDigits(preference)
  return preference
}
const userKey = (user: string) => `${DEVICE_KEY}/user/${user}`

function parse(raw: string | null): LocalePreference | null {
  try {
    const value: unknown = JSON.parse(raw ?? "null")
    if (typeof value !== "object" || value === null) return null
    const { locale, digits } = value as Record<string, unknown>
    return (locale === "en" || locale === "ar") && (digits === "latn" || digits === "arab") ? { locale, digits } : null
  } catch {
    return null
  }
}

/** The browser's language as a starting point: Arabic for any `ar-*`, otherwise English. Latin digits. */
export function browserPreference(
  languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages,
): LocalePreference {
  return { locale: languages.some((l) => l.toLowerCase().startsWith("ar")) ? "ar" : "en", digits: "latn" }
}

/** The BCP 47 tag for formatting numbers and dates: `ar-EG` with the chosen numbering system. */
export function formattingTag(preference: LocalePreference): string {
  return preference.locale === "ar" ? `ar-EG-u-nu-${preference.digits}` : "en"
}

/**
 * Apply a preference to the document: language, direction (Arabic is right-to-left) and the
 * formatting tag other code reads. Layout mirrors through logical CSS properties.
 */
export function applyPreference(preference: LocalePreference, root: HTMLElement = document.documentElement): void {
  root.lang = preference.locale === "ar" ? "ar" : "en"
  root.dir = preference.locale === "ar" ? "rtl" : "ltr"
  root.dataset.digits = preference.digits
}

interface LocaleValue {
  readonly preference: LocalePreference
  readonly tag: string
  setPreference(next: LocalePreference): void
  /** Bind the preference to a user (the cell's principal): theirs is loaded, and saved from now on. */
  setUser(user: string | null): void
}

const LocaleContext = createContext<LocaleValue | null>(null)

/**
 * The console's locale. Until the cell's per-user preferences exist (#3264), a user's choice is kept
 * on this device, keyed by their principal; the device's last choice (or the browser language) is used
 * before anyone is known.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<string | null>(null)
  const [preference, setPreferenceState] = useState<LocalePreference>(initialPreference)

  useEffect(() => {
    applyPreference(preference)
    void instance.changeLanguage(preference.locale)
  }, [preference])

  const setPreference = useCallback(
    (next: LocalePreference) => {
      applyDigits(next)
      setPreferenceState(next)
      localStorage.setItem(DEVICE_KEY, JSON.stringify(next))
      if (user) localStorage.setItem(userKey(user), JSON.stringify(next))
    },
    [user],
  )
  const setUser = useCallback((next: string | null) => {
    setUserState(next)
    const saved = next ? parse(localStorage.getItem(userKey(next))) : null
    if (saved) {
      applyDigits(saved)
      setPreferenceState(saved)
    }
  }, [])

  const value = useMemo(
    () => ({ preference, tag: formattingTag(preference), setPreference, setUser }),
    [preference, setPreference, setUser],
  )
  return (
    <I18nextProvider i18n={instance}>
      <LocaleContext.Provider value={value}>
        {/* Both control libraries read direction from context, including portalled content. */}
        <Direction.Provider dir={preference.locale === "ar" ? "rtl" : "ltr"}>
          <DirectionProvider direction={preference.locale === "ar" ? "rtl" : "ltr"}>{children}</DirectionProvider>
        </Direction.Provider>
      </LocaleContext.Provider>
    </I18nextProvider>
  )
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext)
  if (!value) throw new Error("useLocale outside LocaleProvider")
  return value
}

/** Typed translations: `t("nav.overview")`, `t("findings.count", { count })`. Re-renders when the digits change. */
export function useT() {
  useContext(LocaleContext)
  return useTranslation().t
}

/** Numbers in the operator's locale and digits (counts and labels; never exact data values). */
export function useFormatNumber(): (value: number) => string {
  const { tag } = useLocale()
  return useMemo(() => {
    const format = new Intl.NumberFormat(tag)
    return (value: number) => format.format(value)
  }, [tag])
}

/**
 * Text kept in state until it is shown: a catalog message, translated when rendered so it follows a
 * language switch, or text from elsewhere (the cell, the browser, a library) shown as it came.
 */
export type Notice = SharedNotice<MessageKey>

/** An error whose operator-facing text is a catalog message; its `message` is the English text, for logs. */
export class NoticeError extends SharedNoticeError<MessageKey> {
  constructor(notice: Extract<Notice, { key: MessageKey }>) {
    super(notice, (key, values) => String(instance.t(key as never, { ...values, lng: "en" } as never)))
  }
}

/** Render notices (one, or several joined by spaces) in the current language. */
export function useNotice(): (notice: Notice | readonly Notice[]) => string {
  const t = useT()
  return useCallback(
    (notice: Notice | readonly Notice[]) =>
      formatNotice(notice, (key, values) => String(t(key as never, (values ?? {}) as never))),
    [t],
  )
}

export type { MessageKey }
